import { create } from 'zustand';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TriageMachineMetadata {
  model_name: TriageFieldConfidence;
  series: TriageFieldConfidence;
  scope?: TriageFieldConfidence;
  manufacturer: TriageFieldConfidence;
  category: TriageFieldConfidence;
  doc_type: TriageFieldConfidence;
  version: TriageFieldConfidence;
  language: TriageFieldConfidence;
  raw_text_used?: string;
}

export interface TriageFieldConfidence {
  value: string | null;
  confidence: number;
  source?: string | null;
  learned_from?: string | null;
  conflict_value?: string | null;
  conflict_confidence?: number | null;
  conflict_source?: string | null;
}

export type TriageEditableField =
  | 'doc_type'
  | 'model_name'
  | 'series'
  | 'manufacturer'
  | 'category'
  | 'version'
  | 'language';

export interface TriageResultDto {
  doc_id: string;
  file_name: string;
  doc_type: string | null;
  doc_type_confidence: number;
  subject: string | null;
  summary: string | null;
  language: string | null;
  machine_metadata: TriageMachineMetadata;
  overall_confidence: number;
  triage_status: 'pending' | 'confirmed' | 'rejected';
}

export interface TriageConfirmItem {
  doc_id: string;
  confirmed: boolean;
  overridden_scope?: string | null;
  overridden_doc_type?: string | null;
  overridden_model_name?: string | null;
  overridden_series?: string | null;
  overridden_manufacturer?: string | null;
  overridden_category?: string | null;
  overridden_version?: string | null;
  overridden_language?: string | null;
  mapping_evidence?: DropMappingEvidence[];
}

export interface DropMappingEvidence {
  source_text: string;
  target_field: TriageEditableField;
  applied_value: string;
}

export type TriageSourceType = 'connector' | 'upload';

// ─── Store State ────────────────────────────────────────────────────────────

interface TriageState {
  /** Triage results keyed by sourceKey (connectorId or "upload:{workspaceId}") */
  results: Record<string, TriageResultDto[]>;
  /** Triage status per sourceKey */
  status: Record<string, 'idle' | 'running' | 'awaiting_confirmation' | 'confirmed'>;
  /** Progress tracking */
  progress: Record<string, { processed: number; total: number; currentFile: string } | undefined>;
  /** Upload-only drag/drop evidence keyed by sourceKey/docId */
  mappingEvidence: Record<string, Record<string, DropMappingEvidence[]>>;

  // Actions
  setTriageResults: (sourceKey: string, results: TriageResultDto[]) => void;
  appendTriageResults: (sourceKey: string, newResults: TriageResultDto[]) => void;
  setTriageStatus: (sourceKey: string, status: 'idle' | 'running' | 'awaiting_confirmation' | 'confirmed') => void;
  setTriageProgress: (sourceKey: string, processed: number, total: number, currentFile: string) => void;
  updateResultStatus: (sourceKey: string, docId: string, status: 'confirmed' | 'rejected') => void;
  updateResultField: (sourceKey: string, docId: string, field: TriageEditableField, value: string) => void;
  recordDropMappingEvidence: (sourceKey: string, docId: string, evidence: DropMappingEvidence) => void;
  confirmAll: (sourceKey: string) => void;
  confirmSelected: (sourceKey: string, docIds: string[]) => void;
  pruneTriageResults: (sourceKey: string, validDocIds: string[]) => number;
  clearTriage: (sourceKey: string) => void;
  triggerConnectorTriage: (workspaceId: string, connectorId: string, connectorType: string, configJson: string, token?: string | null) => Promise<boolean>;
  triggerUploadTriage: (workspaceId: string, documentIds: string[], token?: string | null) => Promise<boolean>;
  submitConnectorConfirmation: (workspaceId: string, connectorId: string, token?: string | null) => Promise<boolean>;
  submitUploadConfirmation: (workspaceId: string, token?: string | null) => Promise<boolean>;
  fetchConnectorResults: (workspaceId: string, connectorId: string, token?: string | null) => Promise<void>;
}

function buildHeaders(token?: string | null): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Build the upload sourceKey from a workspaceId */
export function uploadSourceKey(workspaceId: string): string {
  return `upload:${workspaceId}`;
}

function normalizeOverride(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export const useTriageStore = create<TriageState>((set, get) => ({
  results: {},
  status: {},
  progress: {},
  mappingEvidence: {},

  setTriageResults: (sourceKey, results) => {
    set((s) => ({
      results: { ...s.results, [sourceKey]: results },
      status: { ...s.status, [sourceKey]: 'awaiting_confirmation' },
    }));
  },

  appendTriageResults: (sourceKey, newResults) => {
    set((s) => {
      const existing = s.results[sourceKey] || [];
      const existingIds = new Set(existing.map((r) => r.doc_id));
      const toAdd = newResults.filter((r) => !existingIds.has(r.doc_id));
      return {
        results: { ...s.results, [sourceKey]: [...existing, ...toAdd] },
        status: { ...s.status, [sourceKey]: 'awaiting_confirmation' },
      };
    });
  },

  setTriageStatus: (sourceKey, status) => {
    set((s) => ({ status: { ...s.status, [sourceKey]: status } }));
  },

  setTriageProgress: (sourceKey, processed, total, currentFile) => {
    set((s) => ({
      progress: { ...s.progress, [sourceKey]: { processed, total, currentFile } },
    }));
  },

  updateResultStatus: (sourceKey, docId, status) => {
    set((s) => {
      const list = (s.results[sourceKey] || []).map((r) =>
        r.doc_id === docId ? { ...r, triage_status: status } : r,
      );
      return { results: { ...s.results, [sourceKey]: list } };
    });
  },

  updateResultField: (sourceKey, docId, field, value) => {
    set((s) => {
      const list = (s.results[sourceKey] || []).map((r) => {
        if (r.doc_id !== docId) return r;

        let updated = { ...r };

        if (field === 'doc_type') {
          updated = {
            ...updated,
            doc_type: value || null,
            doc_type_confidence: value ? 1.0 : 0.0,
            machine_metadata: {
              ...updated.machine_metadata,
              doc_type: { value: value || null, confidence: value ? 1.0 : 0.0 },
            },
          };
        } else if (field === 'language') {
          updated = {
            ...updated,
            language: value || null,
            machine_metadata: {
              ...updated.machine_metadata,
              language: { value: value || null, confidence: value ? 1.0 : 0.0 },
            },
          };
        } else {
          const metadataField = field as Exclude<TriageEditableField, 'doc_type' | 'language'>;
          updated = {
            ...updated,
            machine_metadata: {
              ...updated.machine_metadata,
              [metadataField]: { value: value || null, confidence: 1.0 },
            },
          };
        }

        // Recalculate overall_confidence from doc_type + model_name
        const dtConf = updated.doc_type_confidence;
        const mnConf = updated.machine_metadata.model_name?.confidence ?? 0;
        updated.overall_confidence = mnConf > 0 ? (dtConf + mnConf) / 2 : dtConf;

        return updated;
      });
      return { results: { ...s.results, [sourceKey]: list } };
    });
  },

  recordDropMappingEvidence: (sourceKey, docId, evidence) => {
    set((s) => {
      const existingForSource = s.mappingEvidence[sourceKey] || {};
      const existingForDoc = existingForSource[docId] || [];
      const filtered = existingForDoc.filter((item) => item.target_field !== evidence.target_field);

      return {
        mappingEvidence: {
          ...s.mappingEvidence,
          [sourceKey]: {
            ...existingForSource,
            [docId]: [...filtered, evidence],
          },
        },
      };
    });
  },

  confirmAll: (sourceKey) => {
    set((s) => {
      const list = (s.results[sourceKey] || []).map((r) => ({
        ...r,
        triage_status: 'confirmed' as const,
      }));
      return { results: { ...s.results, [sourceKey]: list } };
    });
  },

  confirmSelected: (sourceKey, docIds) => {
    const docIdSet = new Set(docIds);
    set((s) => {
      const list = (s.results[sourceKey] || []).map((r) =>
        docIdSet.has(r.doc_id) ? { ...r, triage_status: 'confirmed' as const } : r,
      );
      return { results: { ...s.results, [sourceKey]: list } };
    });
  },

  pruneTriageResults: (sourceKey, validDocIds) => {
    const validDocIdSet = new Set(validDocIds);
    let remaining = 0;

    set((s) => {
      const existing = s.results[sourceKey];
      if (!existing) {
        return s;
      }

      const filtered = existing.filter((result) => validDocIdSet.has(result.doc_id));
      remaining = filtered.length;

      if (filtered.length === existing.length) {
        return s;
      }

      if (filtered.length === 0) {
        const { [sourceKey]: _r, ...restResults } = s.results;
        const { [sourceKey]: _s, ...restStatus } = s.status;
        const { [sourceKey]: _p, ...restProgress } = s.progress;
        const { [sourceKey]: _m, ...restEvidence } = s.mappingEvidence;
        return { results: restResults, status: restStatus, progress: restProgress, mappingEvidence: restEvidence };
      }

      return {
        results: { ...s.results, [sourceKey]: filtered },
      };
    });

    return remaining;
  },

  clearTriage: (sourceKey) => {
    set((s) => {
      const { [sourceKey]: _r, ...restResults } = s.results;
      const { [sourceKey]: _s, ...restStatus } = s.status;
      const { [sourceKey]: _p, ...restProgress } = s.progress;
      const { [sourceKey]: _m, ...restEvidence } = s.mappingEvidence;
      return { results: restResults, status: restStatus, progress: restProgress, mappingEvidence: restEvidence };
    });
  },

  // ── Connector-specific API calls ────────────────────────────────────────

  triggerConnectorTriage: async (workspaceId, connectorId, connectorType, configJson, token) => {
    set((s) => ({ status: { ...s.status, [connectorId]: 'running' } }));
    try {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        connector_type: connectorType,
        config_json: configJson,
      });
      const res = await fetch(
        `/api/python/connectors/${connectorId}/triage?${params}`,
        { method: 'POST', headers: buildHeaders(token) },
      );
      return res.ok;
    } catch {
      set((s) => ({ status: { ...s.status, [connectorId]: 'idle' } }));
      return false;
    }
  },

  submitConnectorConfirmation: async (_workspaceId, connectorId, token) => {
    const results = get().results[connectorId] || [];
    const items: TriageConfirmItem[] = results.map((r) => ({
      doc_id: r.doc_id,
      confirmed: r.triage_status === 'confirmed',
      overridden_scope: normalizeOverride(r.machine_metadata?.scope?.value),
      overridden_doc_type: normalizeOverride(r.doc_type),
      overridden_model_name: normalizeOverride(r.machine_metadata?.model_name?.value),
      overridden_series: normalizeOverride(r.machine_metadata?.series?.value),
      overridden_manufacturer: normalizeOverride(r.machine_metadata?.manufacturer?.value),
      overridden_category: normalizeOverride(r.machine_metadata?.category?.value),
      overridden_version: normalizeOverride(r.machine_metadata?.version?.value),
      overridden_language: normalizeOverride(r.language ?? r.machine_metadata?.language?.value),
    }));
    try {
      const res = await fetch(
        `/api/python/connectors/${connectorId}/triage/confirm`,
        {
          method: 'POST',
          headers: buildHeaders(token),
          body: JSON.stringify({ items }),
        },
      );
      if (res.ok) {
        set((s) => ({ status: { ...s.status, [connectorId]: 'confirmed' } }));
      }
      return res.ok;
    } catch {
      return false;
    }
  },

  fetchConnectorResults: async (_workspaceId, connectorId, token) => {
    try {
      const res = await fetch(
        `/api/python/connectors/${connectorId}/triage/results`,
        { headers: buildHeaders(token) },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.results) {
        set((s) => ({
          results: { ...s.results, [connectorId]: data.results },
          status: { ...s.status, [connectorId]: data.triage_status || 'awaiting_confirmation' },
        }));
      }
    } catch {
      // ignore
    }
  },

  // ── Upload-specific API calls ───────────────────────────────────────────

  triggerUploadTriage: async (workspaceId, documentIds, token) => {
    const key = uploadSourceKey(workspaceId);
    // Clear any stale results from a previous triage batch so that only
    // the new documents appear in the classification dialog.
    set((s) => ({
      results: { ...s.results, [key]: [] },
      status: { ...s.status, [key]: 'running' },
      progress: { ...s.progress, [key]: undefined },
    }));
    try {
      const res = await fetch(
        '/api/dotnet/documents/triage/trigger',
        {
          method: 'POST',
          headers: buildHeaders(token),
          body: JSON.stringify({ workspaceId, documentIds }),
        },
      );
      return res.ok;
    } catch {
      set((s) => ({ status: { ...s.status, [key]: 'idle' } }));
      return false;
    }
  },

  submitUploadConfirmation: async (workspaceId, token) => {
    const key = uploadSourceKey(workspaceId);
    const results = get().results[key] || [];
    const evidenceByDoc = get().mappingEvidence[key] || {};
    const items: TriageConfirmItem[] = results.map((r) => ({
      doc_id: r.doc_id,
      confirmed: r.triage_status === 'confirmed',
      overridden_scope: normalizeOverride(r.machine_metadata?.scope?.value),
      overridden_doc_type: normalizeOverride(r.doc_type),
      overridden_model_name: normalizeOverride(r.machine_metadata?.model_name?.value),
      overridden_series: normalizeOverride(r.machine_metadata?.series?.value),
      overridden_manufacturer: normalizeOverride(r.machine_metadata?.manufacturer?.value),
      overridden_category: normalizeOverride(r.machine_metadata?.category?.value),
      overridden_version: normalizeOverride(r.machine_metadata?.version?.value),
      overridden_language: normalizeOverride(r.language ?? r.machine_metadata?.language?.value),
      mapping_evidence: (evidenceByDoc[r.doc_id] || []).filter((item) => {
        const currentValue = (() => {
          if (item.target_field === 'doc_type') return normalizeOverride(r.doc_type);
          if (item.target_field === 'language') return normalizeOverride(r.language ?? r.machine_metadata?.language?.value);
          return normalizeOverride(r.machine_metadata?.[item.target_field]?.value);
        })();
        return currentValue === normalizeOverride(item.applied_value);
      }),
    }));
    try {
      const res = await fetch(
        '/api/dotnet/documents/triage/confirm',
        {
          method: 'POST',
          headers: buildHeaders(token),
          body: JSON.stringify({ workspaceId, items }),
        },
      );
      if (res.ok) {
        set((s) => ({ status: { ...s.status, [key]: 'confirmed' } }));
      }
      return res.ok;
    } catch {
      return false;
    }
  },
}));
